import { Entry, HistoryType, Person } from '@weave/schema-gen-ts/dist/schemas/comm/history/v1/history_service.pb';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/comm/shared/v1/enums.pb';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { PersonHelpers } from '@frontend/api-person';
import { getWeaveToken } from '@frontend/auth-helpers';
import { ActionsUI } from '@frontend/contact-actions';
import appConfig from '@frontend/env';
import { i18next, useTranslation } from '@frontend/i18n';
import { CachedAudioScrubber } from '@frontend/phone-panels';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppFlagStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { HintedString } from '@frontend/types';
import { theme } from '@frontend/theme';
import {
  MessageIcon,
  NakedButton,
  PhoneIncomingIcon,
  PhoneMissedIcon,
  PhoneOutgoingIcon,
  Text,
  styles,
} from '@frontend/design-system';

dayjs.extend(calendar);

type CallStatus = HintedString<'missed' | 'abandoned' | 'answered'>;
type PhoneHistoryEntryProps = Pick<Entry, 'direction' | 'recordDetails' | 'timestamp' | 'locationUser' | 'recordId'>;
type SmsHistoryEntryProps = Pick<Entry, 'person' | 'personPhone' | 'recordDetails' | 'timestamp' | 'locationPhone'>;

const getPersonName = (person?: Person) => {
  if (person?.lastName && person?.firstName) {
    return PersonHelpers.getFullName({
      FirstName: person?.firstName,
      LastName: person?.lastName,
      PreferredName: person?.preferredName,
    });
  } else {
    return i18next.t('Unknown employee', { ns: 'contacts' });
  }
};

const PhoneIcon = ({ direction, status }: { direction?: Direction; status?: CallStatus }) => {
  const isInbound = direction === Direction.DIRECTION_INBOUND;
  const isOutbound = direction === Direction.DIRECTION_OUTBOUND;

  if (isInbound && status !== 'answered') {
    return <PhoneMissedIcon css={{ paddingRight: theme.spacing(1) }} color='error' />;
  }
  if (isInbound) {
    return <PhoneIncomingIcon css={{ paddingRight: theme.spacing(1) }} />;
  }
  if (isOutbound) {
    return <PhoneOutgoingIcon css={{ paddingRight: theme.spacing(1) }} />;
  }
  return null;
};

const getPhoneText = ({
  direction,
  status,
  locationUser,
}: {
  direction?: Direction;
  status?: CallStatus;
  locationUser?: Entry['locationUser'];
}) => {
  const isInbound = direction === Direction.DIRECTION_INBOUND;
  const isOutbound = direction === Direction.DIRECTION_OUTBOUND;

  if (isInbound && status !== 'answered') {
    return i18next.t('Missed call', { ns: 'contacts' });
  }
  if (isInbound) {
    return i18next.t('Call answered by {{person}}', { person: getPersonName(locationUser), ns: 'contacts' });
  }
  if (isOutbound) {
    return i18next.t('Call placed by {{person}}', {
      person: getPersonName(locationUser),
      ns: 'contacts',
    });
  }
  return i18next.t('Unknown', { ns: 'contacts' });
};

const EntryBody = ({ timestamp, body }: { timestamp?: string; body?: string }) => (
  <div>
    <Text textAlign='left' weight='bold'>
      {dayjs(timestamp).calendar()}
    </Text>
    <Text textAlign='left' color='light' css={styles.multilineTruncate(2)}>
      {body}
    </Text>
  </div>
);

export const PhoneHistoryEntry = ({
  direction,
  recordDetails,
  timestamp,
  locationUser,
  recordId,
}: PhoneHistoryEntryProps) => {
  const weaveToken = getWeaveToken();

  return (
    <div css={historyEntryContainerStyles} data-trackingid='contact-2.0-tray-tab-history-call'>
      <PhoneIcon direction={direction} status={recordDetails?.status} />
      <div>
        <EntryBody
          timestamp={timestamp}
          body={getPhoneText({ direction, locationUser, status: recordDetails?.status })}
        />
        {recordDetails && recordDetails.recordingPath && recordId && recordDetails.status === 'answered' && (
          <CachedAudioScrubber
            trackingId='nwx-phones-contactPanel-history-row-callRecording-audioScrubber'
            filePath={`${appConfig.BACKEND_API}/portal/v1/phone/callRecordings/${recordId}?location_id=${recordDetails.locationId}&token=${weaveToken}`}
            mediaId={recordId}
            css={{ maxWidth: '100%' }}
          />
        )}
      </div>
    </div>
  );
};

export const SmsHistoryEntry = ({
  person,
  personPhone,
  recordDetails,
  timestamp,
  locationPhone,
}: SmsHistoryEntryProps) => {
  const { setShow } = useSlidePanelShallowStore('setShow');
  const mediumMax = useMatchMedia({ maxWidth: breakpoints.medium.max });
  const { onClick } = ActionsUI.actions.useMessageAction();
  const { t } = useTranslation('contacts');
  const { getFeatureFlagValue } = useAppFlagStore();
  const departmentsIsDeprecated = getFeatureFlagValue({
    locationIds: [recordDetails?.locationId ?? ''],
    flagName: 'deprecate-departments',
    strategy: 'ALL',
  });

  const handleClick = () => {
    onClick({
      personId: person?.personId,
      threadGroupId: recordDetails?.locationId,
      groupIds: recordDetails?.locationId ? [recordDetails.locationId] : undefined,
      personPhone,
      departmentId: departmentsIsDeprecated ? undefined : recordDetails?.departmentId,
      locationPhone: departmentsIsDeprecated ? locationPhone : undefined,
      targetSmsData:
        recordDetails?.messageId && timestamp
          ? {
              id: recordDetails.messageId,
              createdAt: timestamp,
            }
          : undefined,
    });
    if (mediumMax) {
      setShow(false);
    }
  };

  return (
    <NakedButton
      css={historyEntryContainerStyles}
      onClick={handleClick}
      trackingId='contact-2.0-tray-tab-history-message'
    >
      <MessageIcon css={{ paddingRight: theme.spacing(1) }} />
      <EntryBody timestamp={timestamp} body={recordDetails?.body || t('No message content')} />
    </NakedButton>
  );
};

export const OldHistoryEntry = (historyEntry: Entry) => {
  const isSms = historyEntry.type === HistoryType.HISTORY_TYPE_SMS;
  const isPhone = historyEntry.type === HistoryType.HISTORY_TYPE_PHONE;

  if (isSms) {
    return <SmsHistoryEntry {...historyEntry} />;
  }
  if (isPhone) {
    return <PhoneHistoryEntry {...historyEntry} />;
  }
  return null;
};

const historyEntryContainerStyles = {
  backgroundColor: 'white',
  borderBottom: `1px solid ${theme.colors.neutral20}`,
  display: 'flex',
  minHeight: 105,
  padding: theme.spacing(2),
  width: '100%',
};
